import {
    DossierDetailType,
    DossierStatusEnum,
    EstablishmentStatus,
    InstitutionalAffiliationType,
    OverViewDossierType,
    Session,
    dossierStatusColorsType,
    establishementStatusEnum,
} from "../../../../types";
import { Box, Typography } from "@mui/material";
import LinearProgressWithLabel from "../../../UIElements/LinearProgressWithLabel";
import CustomButton from "../../../UIElements/CustomButton";
import OverViewDossier from "./OverViewDossier";
import {
    computeLinearProgressValueOfInstruction,
    getColorByPercent,
} from "../utils";
import Dossier from "../Dossier";
import { useContext } from "react";
import { LayoutBarContext } from "../../../../contexts";
import { useLocalStorage } from "../../../../hooks";
import {
    dateFormat,
    processingStepThresholdKey,
    sessionStorageKey,
    settingStorageKey,
} from "../../../../consts";
import { formatDate } from "../../../../lib";
import { ValidatorDecisionInfosType } from "./OverViewDossierWrapper";

type OverViewDossierClientProps = {
    dossierDetail: DossierDetailType;
    linearProgressValue: number;
    disablePaymentBtn?: boolean;
    isPayed?: boolean;
    _dossierStatusColors: dossierStatusColorsType;
    downloadCertificate?: Function;
    showDownloadCertificateBtn?: boolean;
    validatorDecision?: EstablishmentStatus;
    validatorDecisionInfos?: ValidatorDecisionInfosType;
    isQualifed?: boolean;
    showTitle?: boolean;
    institutionalAffiliation?: InstitutionalAffiliationType;
};

export default function OverViewDossierClient({
    dossierDetail,
    linearProgressValue,
    disablePaymentBtn,
    isPayed,
    _dossierStatusColors,
    showDownloadCertificateBtn,
    downloadCertificate,
    validatorDecision,
    validatorDecisionInfos,
    isQualifed,
    showTitle,
    institutionalAffiliation,
}: OverViewDossierClientProps) {
    const { updateContent } = useContext(LayoutBarContext);

    const { storedValue: session } =
        useLocalStorage<Session>(sessionStorageKey);

    const {
        [settingStorageKey]: [...settings],
    } = session;

    const processingStepThreshold = Number(
        (settings.find((s) => s.key === processingStepThresholdKey) || {}).value
    );

    const showValidatorDecision =
        validatorDecision &&
        validatorDecision.code !== establishementStatusEnum.CANDIDAT;

    // ########## clientSpace : overview dossier infos client
    const buildOverviewDossierInfosClient = (
        dossierDetail: DossierDetailType
    ): OverViewDossierType => {
        const labelText =
            dossierDetail.dossierStatus.code === DossierStatusEnum.CREATION
                ? "Compléter mon dossier"
                : "Consulter mon dossier";
        const submittingInfos = dossierDetail.processingStep
            ? {
                  date: dossierDetail.submittedAt
                      ? formatDate(dossierDetail.submittedAt, dateFormat)
                      : "",
                  processStep: dossierDetail.processingStep,
              }
            : null;

        return {
            status: dossierDetail.dossierStatus.label,
            title: showTitle
                ? dossierDetail.establishment.companyName
                : `Dossier de qualification pour ${dossierDetail.referenciel.name}`,
            subTitle: showTitle
                ? `Dossier de qualification pour ${dossierDetail.referenciel.name}`
                : "",
            dossierId: dossierDetail?.id,
            isPayed,
            _dossierStatusColors,
            disablePaymentBtn,
            showDownloadCertificateBtn,
            institutionalAffiliation,
            commitmentsCompleted: [
                DossierStatusEnum.CREATION,
                DossierStatusEnum.SOUMIS,
            ].includes(dossierDetail?.dossierStatus?.code as DossierStatusEnum)
                ? dossierDetail.commitmentsCompleted
                : computeLinearProgressValueOfInstruction(
                      dossierDetail.referenciel.commitments || []
                  ),
            content: (
                <>
                    {!showValidatorDecision ? (
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-start",
                                gap: "0.5rem",
                                alignSelf: "stretch",
                            }}
                        >
                            <Typography
                                sx={{
                                    color: "#555A5D",

                                    fontFamily: "Roboto",
                                    fontSize: "0.875rem",
                                    fontStyle: "normal",
                                    fontWeight: 400,
                                    lineHeight: "normal",
                                    alignSelf: "stretch",
                                }}
                            >
                                Complétude des engagements
                            </Typography>
                            <LinearProgressWithLabel
                                value={linearProgressValue}
                                classes={{
                                    rootsx: {
                                        width: "100%",
                                    },
                                    linearProgressSx: {
                                        background: "#E3E6E8",
                                        ".MuiLinearProgress-bar": {
                                            backgroundColor:
                                                getColorByPercent(
                                                    linearProgressValue
                                                ),
                                        },
                                    },
                                }}
                            />
                        </Box>
                    ) : null}
                    {submittingInfos ? (
                        <Box
                            sx={{
                                display: "flex",
                                // flexDirection: "column",
                                alignItems: "flex-start",
                                gap: "1rem",
                                alignSelf: "stretch",
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "flex-start",
                                    gap: "0.5rem",
                                    alignSelf: "stretch",
                                    flex: "1 0 0",
                                }}
                            >
                                <Typography
                                    sx={{
                                        color: "#555A5D",

                                        fontFamily: "Roboto",
                                        fontSize: "0.875rem",
                                        fontStyle: "normal",
                                        fontWeight: 400,
                                        lineHeight: "normal",
                                        alignSelf: "stretch",
                                    }}
                                >
                                    Date de soumission
                                </Typography>
                                <Typography
                                    variant="h1"
                                    sx={{
                                        color: "#555A5D",

                                        fontFamily: "Roboto",
                                        fontSize: "1rem",
                                        fontStyle: "normal",
                                        fontWeight: 500,
                                        lineHeight: "normal",
                                        alignSelf: "stretch",
                                    }}
                                >
                                    <b>{submittingInfos.date}</b>
                                </Typography>
                            </Box>
                            {!showValidatorDecision ? (
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "flex-start",
                                        gap: "0.5rem",
                                        alignSelf: "stretch",
                                        flex: "1 0 0",
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            color: "#555A5D",

                                            fontFamily: "Roboto",
                                            fontSize: "0.875rem",
                                            fontStyle: "normal",
                                            fontWeight: 400,
                                            lineHeight: "normal",
                                            alignSelf: "stretch",
                                        }}
                                    >
                                        Aller/retour
                                    </Typography>
                                    <Typography
                                        variant="h1"
                                        sx={{
                                            color: "#555A5D",

                                            fontFamily: "Roboto",
                                            fontSize: "1rem",
                                            fontStyle: "normal",
                                            fontWeight: 500,
                                            lineHeight: "normal",
                                            alignSelf: "stretch",
                                        }}
                                    >
                                        <b>{`${submittingInfos.processStep}/${processingStepThreshold}`}</b>
                                    </Typography>
                                </Box>
                            ) : null}
                        </Box>
                    ) : null}

                    {validatorDecisionInfos && isQualifed ? (
                        <Box
                            sx={{
                                display: "flex",
                                // flexDirection: "column",
                                alignItems: "flex-start",
                                gap: "1rem",
                                alignSelf: "stretch",
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "flex-start",
                                    gap: "0.5rem",
                                    alignSelf: "stretch",
                                    flex: "1 0 0",
                                }}
                            >
                                <Typography
                                    sx={{
                                        color: "#555A5D",

                                        fontFamily: "Roboto",
                                        fontSize: "0.875rem",
                                        fontStyle: "normal",
                                        fontWeight: 400,
                                        lineHeight: "normal",
                                        alignSelf: "stretch",
                                    }}
                                >
                                    Date de qualification
                                </Typography>
                                <Typography
                                    variant="h1"
                                    sx={{
                                        color: "#555A5D",

                                        fontFamily: "Roboto",
                                        fontSize: "1rem",
                                        fontStyle: "normal",
                                        fontWeight: 500,
                                        lineHeight: "normal",
                                        alignSelf: "stretch",
                                    }}
                                >
                                    <b>
                                        {
                                            validatorDecisionInfos.assignedValidatorDecisionAt
                                        }
                                    </b>
                                </Typography>
                            </Box>
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "flex-start",
                                    gap: "0.5rem",
                                    alignSelf: "stretch",
                                    flex: "1 0 0",
                                }}
                            >
                                <Typography
                                    sx={{
                                        color: "#555A5D",

                                        fontFamily: "Roboto",
                                        fontSize: "0.875rem",
                                        fontStyle: "normal",
                                        fontWeight: 400,
                                        lineHeight: "normal",
                                        alignSelf: "stretch",
                                    }}
                                >
                                    Date d’expiration
                                </Typography>
                                <Typography
                                    variant="h1"
                                    sx={{
                                        color: "#555A5D",

                                        fontFamily: "Roboto",
                                        fontSize: "1rem",
                                        fontStyle: "normal",
                                        fontWeight: 500,
                                        lineHeight: "normal",
                                        alignSelf: "stretch",
                                    }}
                                >
                                    <b>
                                        {
                                            validatorDecisionInfos.expirationCertificateAt
                                        }
                                    </b>
                                </Typography>
                            </Box>
                        </Box>
                    ) : null}

                    {showValidatorDecision ? (
                        <Box
                            sx={{
                                display: "flex",
                                // flexDirection: "column",
                                alignItems: "flex-start",
                                gap: "1rem",
                                alignSelf: "stretch",
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "flex-start",
                                    gap: "0.5rem",
                                    alignSelf: "stretch",
                                    flex: "1 0 0",
                                }}
                            >
                                <Typography
                                    sx={{
                                        color: "#555A5D",

                                        fontFamily: "Roboto",
                                        fontSize: "0.875rem",
                                        fontStyle: "normal",
                                        fontWeight: 400,
                                        lineHeight: "normal",
                                        alignSelf: "stretch",
                                    }}
                                >
                                    Décision SYNERPA
                                </Typography>
                                <Typography
                                    variant="h1"
                                    sx={{
                                        color: "#555A5D",

                                        fontFamily: "Roboto",
                                        fontSize: "1rem",
                                        fontStyle: "normal",
                                        fontWeight: 500,
                                        lineHeight: "normal",
                                        alignSelf: "stretch",
                                    }}
                                >
                                    <b>{validatorDecision.label}</b>
                                </Typography>
                            </Box>
                        </Box>
                    ) : null}
                </>
            ),
            actions: {
                edit: (
                    <CustomButton
                        onClick={() => {
                            if (dossierDetail.id) {
                                updateContent(
                                    <Dossier dossierId={dossierDetail.id} />
                                );
                            }
                        }}
                        label={labelText}
                        customsx={{
                            width: "100%",
                        }}
                    />
                ),
                downloadCertificate,
            },
        };
    };

    return (
        <OverViewDossier
            overViewDossierInfo={buildOverviewDossierInfosClient(dossierDetail)}
        />
    );
}
